import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRouter } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import {
  searchEngine,
  type SearchResult,
  highlightText,
} from "@/lib/search-engine";
import { getAllCommands, commandCategories } from "@/lib/search-commands";
import { announceToScreenReader } from "@/hooks/use-keyboard-shortcuts";
import { cn } from "@/lib/utils";
import {
  Search,
  Command,
  ArrowRight,
  Zap,
  Loader2,
  Milk,
  WheatIcon,
  Package,
  FlaskConical,
  TestTube,
  Calendar,
  SearchIcon,
  Plus,
  FileText,
} from "lucide-react";

interface SearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchPalette({ open, onOpenChange }: SearchPaletteProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const router = useRouter();

  // Fetch searchable items from backend
  const { data: searchItems, isLoading } = trpc.search.getAllItems.useQuery(
    undefined,
    {
      enabled: open,
    },
  );

  // Register commands and items with search engine
  const commands = useMemo(() => getAllCommands(router), [router]);

  useEffect(() => {
    if (searchItems) {
      searchEngine.setItems(searchItems);
      console.log("searchItems", searchItems);
      searchEngine.setCommands(commands);
    }
  }, [searchItems, commands]);

  // Search functionality
  useEffect(() => {
    if (!searchItems) return;

    // Only search when there's a query
    if (query.trim()) {
      const searchResults = searchEngine.search(query, {
        maxResults: 8,
        includeCommands: true,
      });

      setResults(searchResults);
      setSelectedIndex(0);

      // Announce results to screen readers
      const resultCount = searchResults.length;
      if (resultCount === 0) {
        announceToScreenReader(`No results found for ${query}`);
      } else {
        announceToScreenReader(
          `${resultCount} result${resultCount === 1 ? "" : "s"} found for ${query}`,
        );
      }
    } else {
      // Clear results when no query
      setResults([]);
      setSelectedIndex(0);
    }
  }, [query, searchItems]);

  // Get default content when no query
  const getDefaultContent = () => {
    // Prioritize creation and navigation commands
    const createCommands = commands
      .filter((cmd) => cmd.category === "create")
      .slice(0, 4);
    const navCommands = commands
      .filter((cmd) => cmd.category === "navigate")
      .slice(0, 2);

    const quickCommands = [...createCommands, ...navCommands];
    const defaultResults: SearchResult[] = quickCommands.map((command) => ({
      command,
      score: 1,
      matchedFields: [],
      highlightRanges: [],
    }));

    return defaultResults;
  };

  const displayResults = useMemo(() => {
    // Show default content if no query and no search results
    if (!query && results.length === 0) {
      return getDefaultContent();
    }
    return results;
  }, [query, results, commands]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            Math.min(prev + 1, displayResults.length - 1),
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (displayResults[selectedIndex]) {
            handleResultSelect(displayResults[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, displayResults, selectedIndex, onOpenChange]);

  const handleResultSelect = (result: SearchResult) => {
    if (result.command) {
      result.command.action();
      onOpenChange(false);
    } else if (result.item) {
      router.navigate({ to: result.item.url });
      onOpenChange(false);
    }
  };

  const getResultIcon = (result: SearchResult) => {
    if (result.command) {
      // Use the command's own icon if available
      return result.command.icon || Command;
    }

    switch (result.item?.type) {
      case "fermentation_group":
        return Milk;
      case "harvest":
        return WheatIcon;
      case "batch":
        return Package;
      case "protocol":
        return FileText;
      case "inventory_item":
        return Package;
      default:
        return FileText;
    }
  };

  const getResultCategory = (result: SearchResult) => {
    if (result.command) {
      return commandCategories[result.command.category].title;
    }

    switch (result.item?.type) {
      case "fermentation_group":
        return "Fermentation";
      case "harvest":
        return "Harvest";
      case "batch":
        return "Batch";
      case "protocol":
        return "Protocol";
      case "inventory_item":
        return "Inventory";
      default:
        return "Item";
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedIndex(0);
      setResults([]);
    }
  }, [open]);

  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};

    displayResults.forEach((result) => {
      const category = getResultCategory(result);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(result);
    });

    return groups;
  }, [displayResults]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl p-0 gap-0">
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
              y: -10,
              height: 36, // Start with roughly search bar height
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              height: "auto",
            }}
            exit={{
              opacity: 0,
              scale: 0.95,
              y: -10,
              height: 36,
            }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
              height: { duration: 0.25, ease: "easeOut" },
            }}
            className="flex flex-col overflow-hidden"
          >
            {/* Search Header */}
            <div className="flex w-full items-center gap-3 pr-12 p-4 border-b">
              <Search
                className="w-5 h-5 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                placeholder="Search everything... (try 'new fermentation' or 'harvest')"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="border-0 flex-1 focus-visible:ring-0 text-base"
                autoFocus
                role="combobox"
                aria-expanded={results.length > 0}
                aria-activedescendant={
                  selectedIndex >= 0
                    ? `search-result-${selectedIndex}`
                    : undefined
                }
                aria-label="Search BrewBase"
              />
              {isLoading && (
                <Loader2
                  className="w-4 h-4 animate-spin text-muted-foreground"
                  aria-label="Loading search results"
                />
              )}
            </div>

            {/* Results */}
            <div
              className="max-h-96 overflow-y-auto"
              role="listbox"
              aria-label="Search results"
            >
              <AnimatePresence>
                {displayResults.length === 0 && !isLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-8 text-center text-muted-foreground"
                  >
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No results found for "{query}"</p>
                    <p className="text-sm mt-1">
                      Try searching for fermentations, harvests, or inventory
                      items
                    </p>
                  </motion.div>
                ) : !query && displayResults.length > 0 ? (
                  // Default content with hints
                  <div className="p-4">
                    <div className="mb-4 text-center">
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">
                        Quick Actions
                      </h3>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          Try:{" "}
                          <span className="font-mono bg-muted px-1 rounded">
                            "new fermentation"
                          </span>{" "}
                          or{" "}
                          <span className="font-mono bg-muted px-1 rounded">
                            "register item"
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Search for: fermentations, harvests, batches,
                          protocols, inventory
                        </p>
                      </div>
                    </div>
                    {Object.entries(groupedResults).map(
                      ([category, categoryResults], groupIndex) => (
                        <motion.div
                          key={category}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: groupIndex * 0.05 }}
                        >
                          {categoryResults.map((result, index) => {
                            const globalIndex = displayResults.indexOf(result);
                            const isSelected = globalIndex === selectedIndex;

                            return (
                              <motion.div
                                key={result.command?.id || result.item?.id}
                                id={`search-result-${globalIndex}`}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                  delay:
                                    (groupIndex * categoryResults.length +
                                      index) *
                                    0.02,
                                }}
                                className={cn(
                                  "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                                  isSelected
                                    ? "bg-accent"
                                    : "hover:bg-accent/50",
                                )}
                                onClick={() => handleResultSelect(result)}
                                role="option"
                                aria-selected={isSelected}
                                aria-label={`${result.command?.title || result.item?.title}${result.command?.description || result.item?.subtitle ? ` - ${result.command?.description || result.item?.subtitle}` : ""}`}
                              >
                                <div className="flex items-center justify-center w-8 h-8 text-muted-foreground">
                                  {React.createElement(getResultIcon(result), {
                                    size: 16,
                                  })}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium truncate">
                                      {result.command?.title ||
                                        result.item?.title}
                                    </h4>

                                    {result.command && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        {
                                          commandCategories[
                                            result.command.category
                                          ].title
                                        }
                                      </Badge>
                                    )}
                                  </div>

                                  {(result.item?.subtitle ||
                                    result.command?.description) && (
                                    <p className="text-sm text-muted-foreground truncate">
                                      {result.item?.subtitle ||
                                        result.command?.description}
                                    </p>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 text-muted-foreground">
                                  {result.command ? (
                                    <Zap className="w-3 h-3" />
                                  ) : (
                                    <ArrowRight className="w-3 h-3" />
                                  )}
                                </div>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="p-2">
                    {Object.entries(groupedResults).map(
                      ([category, categoryResults], groupIndex) => (
                        <motion.div
                          key={category}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: groupIndex * 0.05 }}
                        >
                          {Object.keys(groupedResults).length > 1 && (
                            <>
                              <div className="px-2 py-1 mb-1">
                                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  {category}
                                </h3>
                              </div>
                            </>
                          )}

                          {categoryResults.map((result, index) => {
                            const globalIndex = results.indexOf(result);
                            const isSelected = globalIndex === selectedIndex;

                            return (
                              <motion.div
                                key={result.command?.id || result.item?.id}
                                id={`search-result-${globalIndex}`}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                  delay:
                                    (groupIndex * categoryResults.length +
                                      index) *
                                    0.02,
                                }}
                                className={cn(
                                  "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                                  isSelected
                                    ? "bg-accent"
                                    : "hover:bg-accent/50",
                                )}
                                onClick={() => handleResultSelect(result)}
                                role="option"
                                aria-selected={isSelected}
                                aria-label={`${result.command?.title || result.item?.title}${result.command?.description || result.item?.subtitle ? ` - ${result.command?.description || result.item?.subtitle}` : ""}`}
                              >
                                <div className="flex items-center justify-center w-8 h-8 text-muted-foreground">
                                  {React.createElement(getResultIcon(result), {
                                    size: 16,
                                  })}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium truncate">
                                      {highlightText(
                                        result.command?.title ||
                                          result.item?.title ||
                                          "",
                                        result.highlightRanges.filter(
                                          (r) => r.field === "title",
                                        ),
                                        "bg-primary/20 rounded px-0.5",
                                      )}
                                    </h4>

                                    {result.command && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        {
                                          commandCategories[
                                            result.command.category
                                          ].title
                                        }
                                      </Badge>
                                    )}
                                  </div>

                                  {(result.item?.subtitle ||
                                    result.command?.description) && (
                                    <p className="text-sm text-muted-foreground truncate">
                                      {highlightText(
                                        result.item?.subtitle ||
                                          result.command?.description ||
                                          "",
                                        result.highlightRanges.filter((r) =>
                                          ["subtitle", "description"].includes(
                                            r.field,
                                          ),
                                        ),
                                        "bg-primary/20 rounded px-0.5",
                                      )}
                                    </p>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 text-muted-foreground">
                                  {result.command ? (
                                    <Zap className="w-3 h-3" />
                                  ) : (
                                    <ArrowRight className="w-3 h-3" />
                                  )}
                                </div>
                              </motion.div>
                            );
                          })}

                          {groupIndex <
                            Object.entries(groupedResults).length - 1 && (
                            <Separator className="my-2" />
                          )}
                        </motion.div>
                      ),
                    )}
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-3 border-t bg-muted/30 text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-background border">
                    ↑↓
                  </kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-background border">
                    Enter
                  </kbd>
                  Select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-background border">
                    Esc
                  </kbd>
                  Close
                </span>
              </div>

              <div className="flex items-center gap-1">
                <span>⌘K to open</span>
              </div>
            </div>
          </motion.div>
        </DialogContent>
      </Dialog>
    </>
  );
}
